import { useState, useEffect } from 'react';

interface ConfigIssue {
  severity: 'error' | 'warning';
  message: string;
  fix?: string;
}

/**
 * Validates app configuration and displays issues visually.
 * Helps debug blank screen issues caused by missing config.
 */
export const ConfigValidator = () => {
  const [issues, setIssues] = useState<ConfigIssue[]>([]);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const foundIssues: ConfigIssue[] = [];

    // Check if config.js loaded
    if (!window.ENV) {
      foundIssues.push({
        severity: 'error',
        message: 'Configuration file not loaded (window.ENV is undefined)',
        fix: 'Check if /config.js file exists and is being loaded in index.html',
      });
    } else {
      // Check API URL
      if (!window.ENV.VITE_API_BASE_URL) {
        foundIssues.push({
          severity: 'error',
          message: 'API Base URL is not configured',
          fix: 'Set VITE_API_BASE_URL in config.js',
        });
      } else if (!window.ENV.VITE_API_BASE_URL.startsWith('http')) {
        foundIssues.push({
          severity: 'error',
          message: `Invalid API URL: "${window.ENV.VITE_API_BASE_URL}"`,
          fix: 'API URL must start with http:// or https://',
        });
      }

      // Check environment
      if (!window.ENV.VITE_ENVIRONMENT) {
        foundIssues.push({
          severity: 'warning',
          message: 'Environment not specified',
          fix: 'Set VITE_ENVIRONMENT to "local", "dev", or "prod"',
        });
      }
    }

    // Check if we can reach the API
    if (window.ENV?.VITE_API_BASE_URL) {
      fetch(`${window.ENV.VITE_API_BASE_URL}/api/health`, {
        method: 'GET',
        mode: 'cors',
      })
        .then(response => {
          if (!response.ok) {
            foundIssues.push({
              severity: 'warning',
              message: `API health check failed (status ${response.status})`,
              fix: 'Check if the API is running and accessible',
            });
            setIssues([...foundIssues]);
          }
        })
        .catch(() => {
          foundIssues.push({
            severity: 'warning',
            message: 'Cannot reach API endpoint',
            fix: 'Check network connection and API URL configuration',
          });
          setIssues([...foundIssues]);
        });
    }

    setIssues(foundIssues);

    // Log to console
    if (foundIssues.length > 0) {
      console.error('⚠️ Configuration Issues:', foundIssues);
    } else {
      console.log('✅ Configuration validated successfully');
    }
  }, []);

  if (issues.length === 0 || isDismissed) {
    return null;
  }

  const hasErrors = issues.some(i => i.severity === 'error');

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 ${
      hasErrors ? 'bg-red-600' : 'bg-yellow-600'
    } text-white shadow-lg`}>
      <div className="max-w-4xl mx-auto px-4 py-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center mb-2">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="font-bold">
                Configuration {hasErrors ? 'Error' : 'Warning'}
              </h3>
            </div>
            <div className="space-y-2 text-sm">
              {issues.map((issue, index) => (
                <div key={index} className="pl-7">
                  <p className="font-semibold">{issue.message}</p>
                  {issue.fix && (
                    <p className="text-white/90 text-xs mt-1">→ {issue.fix}</p>
                  )}
                </div>
              ))}
            </div>
            {hasErrors && (
              <p className="mt-3 text-xs text-white/90 pl-7">
                The app may not work correctly until these errors are resolved.
              </p>
            )}
          </div>
          <button
            onClick={() => setIsDismissed(true)}
            className="ml-4 text-white/80 hover:text-white"
            aria-label="Dismiss"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
